<template>
  <!-- BEGIN: Table -->
  <div class="accordion__pane py-4">
    <div class="flex justify-between">
      <a
        href="javascript:;"
        class="accordion__pane__toggle relative font-medium text-2xl text-gray-600 block pr-24 whitespace-nowrap overflow-hidden overflow-ellipsis"
      >
        {{ headerTitle }}
        <template v-if="!loading">({{ list.meta.total }})</template>
        <LoaderIcon v-else class="spin-5 inline-block w-8 h-8" />
      </a>
      <Tippy
        v-if="$_.get(committee, 'abilities.can_update', false)"
        tag="button"
        content="Send Email To Members"
        :options="{ placement: 'bottom' }"
        class="dropdown-toggle button p-0 absolute right-4"
        @click="onSendEmail"
      >
        <SendIcon class="w-8 h-8" />
      </Tippy>
    </div>
    <div class="accordion__pane__content mt-3 leading-relaxed">
      <div v-if="loading" class="text-center p-4 pt-8">
        <LoaderIcon class="spin-5 inline-block w-8 h-8" />
        <div class="mt-2 font-light">
          Please wait while the records are loading.
        </div>
      </div>
      <template v-if="list.data.length > 0">
        <div class="overflow-x-auto mt-3">
          <table class="table w-full">
            <thead>
              <tr>
                <th class="whitespace-nowrap">Role</th>
                <th class="whitespace-nowrap">Name - Surname</th>
                <th class="whitespace-nowrap text-center">Country</th>
                <th class="whitespace-nowrap w-1 text-right">Functions</th>
              </tr>
            </thead>
            <tbody>
              <TransactionStatement
                tag="tr"
                v-for="row in list.data"
                :key="'record-' + row.id"
              >
                <td>{{ row.title }}</td>
                <td>
                  {{ $h.formatNameSurname($_.get(row, "user")) }}
                </td>
                <td class="text-center">
                  {{
                    `${$_.get(
                      row,
                      "user.country.title",
                      $_.get(row, "user.country_code", "")
                    )}`
                  }}
                </td>
                <td class="whitespace-nowrap text-right">
                  <Tippy
                    tag="button"
                    content="See Details"
                    :options="{ placement: 'bottom' }"
                    class="button py-2 px-2 bg-white text-theme-1 rounded-full shadow-lg mr-2"
                    @click="() => goToView($_.get(row, 'user.id', ''))"
                  >
                    <EyeIcon class="inline w-5 h-5" />
                  </Tippy>
                </td>
              </TransactionStatement>
            </tbody>
          </table>
        </div>
        <Pagination
          class="mt-4"
          :current="filter.query.page * 1"
          :per-page="filter.query.paginate * 1"
          :total="$_.get(list, 'meta.total', 0) * 1"
          @update-page="changePage"
          @update-perpage="changePerPage"
        />
      </template>
      <div v-else class="text-lg text-gray-500">
        No users found for this committee!
      </div>
    </div>
  </div>
  <!-- END: Table -->
</template>

<script lang="ts">
import { computed, defineComponent, inject, onMounted, Ref } from "vue";
import { useRouter } from "vue-router";
import { useGeneralActions } from "@/utils/actions";
import { committeeService, GetCommittee } from "@/services";
import TransactionStatement from "@/components/transaction-statement/Main.vue";
import Pagination from "@/components/pagination/Main.vue";
import { SendEmailRef } from "@/components/email-to-members";

export default defineComponent({
  name: "CommitteeUsers",
  components: { Pagination, TransactionStatement },
  props: {
    committeeId: {
      default: () => null,
    },
    committee: {
      type: Object as () => GetCommittee,
      default: () => ({}),
    },
  },
  setup(props) {
    const router = useRouter();
    const sendEmailRef = inject<Ref<SendEmailRef>>(`sendEmailRef`);
    const { fetchRecords, filterRecords } = useGeneralActions();
    const { fetch, loading, list } = fetchRecords({
      fetchApiMethod: async (query: Record<string, any>) => {
        return await committeeService.fetchCommitteeUsers(
          props.committee.id,
          query
        );
      },
    });

    const { filter, formattedQuery, changePage, changePerPage } = filterRecords(
      fetch,
      {
        page: 1,
        paginate: 15,
      },
      true
    );

    const isWg = computed(() => {
      return props.committee.type === "WG";
    });

    const headerTitle = computed(() => {
      if (isWg.value) return "Working Group Users List";
      return "Committee Users List";
    });

    const goToView = (value: any) => {
      router.push({ name: "users.view", params: { id: value } });
    };

    const onSendEmail = () => {
      sendEmailRef?.value.show({
        committee: props.committee,
      });
    };

    onMounted(() => {
      fetch(formattedQuery()).finally();
    });

    return {
      headerTitle,
      loading,
      list,
      filter,
      changePage,
      changePerPage,
      goToView,
      onSendEmail,
    };
  },
});
</script>
